import * as log from '../util/log';
import { storableError } from '../util/errors';
import { util as sdkUtil } from '../util/sdkLoader';
import { LISTING_STATE_DRAFT, LISTING_STATE_PUBLISHED } from '../util/types';
import { ensureOwnListing, denormalisedResponseEntities, getUserDefaultCurrency, getCurrentUserLocationInfo, extractUserCurrencyAndLocation, clearLocationWatch } from '../util/data';
import { getTransitionsNeedingCustomerAttention, getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { fetchCartItems } from './cart.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { fetchUserCountry, getStoreCategoryConfig } from '../util/api';
import { identifyUser, setPublishedListingsCount } from '../util/mixpanel';
import { types as sdkTypes } from '../util/sdkLoader';
const { LatLng, LatLngBounds } = sdkTypes;

import Cookies from 'js-cookie';
import { CURRENCY_COOKIE_KEY, DEFAULT_CURRENCY, LOCATIONINFO_COOKIE_KEY } from '../util/constants';
import { signupCountries } from '../config/configDefault';

const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;

// ================ Action types ================ //
export const SET_CURRENT_USER_CURRENCY = 'app/user/SET_CURRENT_USER_CURRENCY';
export const SET_CURRENT_USER_LOCATION_INFO =
  'app/user/SET_CURRENT_USER_LOCATION_INFO';
export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const SET_STORE_CATEGORY_CONFIG = 'app/user/SET_STORE_CATEGORY_CONFIG';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST =
  'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS =
  'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR =
  'app/user/SEND_VERIFICATION_EMAIL_ERROR';


export const SET_LAST_LISTING_LOCATION = 'app/user/SET_LAST_LISTING_LOCATION';

export const SET_DETECTED_CURRENT_LOCATION = 'app/user/SET_DETECTED_CURRENT_LOCATION';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } =
    oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  detectedCurrentLocation: null,
  lastListingLocation: null,
  currentUserLocationInfo: null,
  currentUserCurrency: 'USD',
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  currentUserSalesNotificationCount: 0,
  currentUserOrdersNotificationCount: 0,
  currentUserSalesUnreadMessages: {},
  currentUserOrdersUnreadMessages: {},
  customerAttentionTxs: [],
  providerAttentionTxs: [],
  /** Store category tree from /api/store-categories/config (Mongo-backed). */
  storeCategoryConfig: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        storeCategoryConfig: null,
      };

    case SET_STORE_CATEGORY_CONFIG:
      return { ...state, storeCategoryConfig: payload };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        currentUserNotificationCount: payload.salesCount + payload.ordersCount,
        currentUserSalesNotificationCount: payload.salesCount,
        currentUserOrdersNotificationCount: payload.ordersCount,
        currentUserSalesUnreadMessages: payload.salesUnreadMessages,
        currentUserOrdersUnreadMessages: payload.ordersUnreadMessages,
        customerAttentionTxs: payload.customerAttentionTxs,
        providerAttentionTxs: payload.providerAttentionTxs,
      };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };
    case SET_CURRENT_USER_CURRENCY:
      return {
        ...state,
        currentUserCurrency: payload,
      };
    case SET_CURRENT_USER_LOCATION_INFO:
      return {
        ...state,
        currentUserLocationInfo: payload,
      };

    case SET_LAST_LISTING_LOCATION:
      return {
        ...state,
        lastListingLocation: payload,
      };
    case SET_DETECTED_CURRENT_LOCATION:
      return {
        ...state,
        detectedCurrentLocation: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //
export const setDetectedCurrentLocation = detectedCurrentLocation => ({
  type: SET_DETECTED_CURRENT_LOCATION,
  payload: detectedCurrentLocation,
});
export const setCurrentUserLocationInfo = locationInfo => ({
  type: SET_CURRENT_USER_LOCATION_INFO,
  payload: locationInfo,
});
export const setCurrentUserCurrency = currency => ({
  type: SET_CURRENT_USER_CURRENCY,
  payload: currency,
});

export const currentUserShowRequest = () => ({
  type: CURRENT_USER_SHOW_REQUEST,
});

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

export const fetchStoreCategoryConfig = () => dispatch => {
  return getStoreCategoryConfig()
    .then(data => {
      dispatch({ type: SET_STORE_CATEGORY_CONFIG, payload: data });
      return data;
    })
    .catch(() => {
      dispatch({ type: SET_STORE_CATEGORY_CONFIG, payload: null });
    });
};

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = (salesCount, ordersCount, salesUnreadMessages, ordersUnreadMessages, customerAttentionTxs, providerAttentionTxs) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { salesCount, ordersCount, salesUnreadMessages, ordersUnreadMessages, customerAttentionTxs, providerAttentionTxs },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const setLastListingLocation = location => ({
  type: SET_LAST_LISTING_LOCATION,
  payload: location,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const { data } = response.data;
      const { geolocation, publicData } = data[0]?.attributes || {};
      const { city, region, country, location, postcode } = publicData || {};
      const { address } = location || {};

      const hasListings = data && data?.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(data?.[0]).attributes.state !==
        LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));

      if (!!geolocation && !!city && !!country && !!location) {
        // Convert country name to ISO code if needed (listings store full names).
        // Match case-insensitively since stored casing can vary between listings.
        const countryData = signupCountries.find(
          c => c.country.toLowerCase() === country.toLowerCase()
        );
        // Only accept an already-2-letter code as a fallback — never pass the raw
        // country name through as a "code", since Intl.DisplayNames.of() throws
        // (caught as "Error getting country name") for anything that isn't a
        // well-formed region code.
        const countryCode =
          countryData?.code || (country.length === 2 ? country.toUpperCase() : null);

        dispatch(setLastListingLocation({
          address,
          city,
          origin: geolocation,
          country: countryCode,
          region,
          postcode,
        }));
      }
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = (currentUser) => (
  dispatch,
  getState,
  sdk
) => {
  const transitionsNeedingProviderAttention = getTransitionsNeedingProviderAttention();
  const transitionsNeedingCustomerAttention = getTransitionsNeedingCustomerAttention();

  if ((transitionsNeedingProviderAttention.length === 0) && (transitionsNeedingCustomerAttention.length === 0)) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQuerySalesParams = {
    only: 'sale',
    last_transitions: transitionsNeedingProviderAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  const apiQueryOrdersParams = {
    only: 'order',
    last_transitions: transitionsNeedingCustomerAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };


  dispatch(fetchCurrentUserNotificationsRequest());
  const salesPromise = sdk.transactions.query(apiQuerySalesParams);
  const ordersPromise = sdk.transactions.query(apiQueryOrdersParams);
  Promise.all([salesPromise, ordersPromise])
    .then(([salesResponse, ordersResponse]) => {
      const salesTransactions = salesResponse.data.data;
      const ordersTransactions = ordersResponse.data.data;
      const { unreadMessages } = currentUser?.attributes?.profile?.protectedData || {};
      const salesUnreadMessages = unreadMessages?.sales || {};
      const ordersUnreadMessages = unreadMessages?.orders || {};
      const salesUnreadMessagesCount = Object.values(salesUnreadMessages).reduce((acc, curr) => acc + curr, 0);
      const ordersUnreadMessagesCount = Object.values(ordersUnreadMessages).reduce((acc, curr) => acc + curr, 0);
      const totalSalesCount = salesTransactions.length + salesUnreadMessagesCount;
      const totalOrdersCount = ordersTransactions.length + ordersUnreadMessagesCount;

      const customerAttentionTxs = salesTransactions.map(tx => tx.id.uuid);
      const providerAttentionTxs = ordersTransactions.map(tx => tx.id.uuid);

      // Need to separate sales and orders transactions to show them in different tabs
      dispatch(fetchCurrentUserNotificationsSuccess(totalSalesCount, totalOrdersCount, salesUnreadMessages, ordersUnreadMessages, customerAttentionTxs, providerAttentionTxs));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

// Helper to identify the user in Mixpanel and update published listings count
const updateMixpanelForCurrentUser = (currentUser, sdk) => {
  // Identify the current user in Mixpanel with basic profile data.
  // identifyUser returns true only the first time per page load.
  const didIdentify = identifyUser(currentUser);

  // If this is not the first identification, skip additional work.
  if (!didIdentify) {
    return Promise.resolve(currentUser);
  }

  // Query only published listings and rely on meta.totalItems for the count
  const listingQueryParams = {
    page: 1,
    perPage: 1,
    states: LISTING_STATE_PUBLISHED,
  };

  return sdk.ownListings
    .query(listingQueryParams)
    .then(listingsResponse => {
      const totalItems = listingsResponse?.data?.meta?.totalItems;
      if (typeof totalItems === 'number') {
        setPublishedListingsCount(totalItems);
      }
      return currentUser;
    })
    .catch(() => currentUser);
};

export const fetchCurrentUser = (params = null) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Need to initiate Intercom for the unauthenticated user
    if (typeof window != 'undefined' && window.Intercom && intercomAppId) {
      const params = {
        app_id: intercomAppId,
      };
      window.Intercom('boot', params);
      // console.log(params, 'Intercom Params')
    }
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error(
          'Expected a resource in the sdk.currentUser.show response'
        );
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(fetchStoreCategoryConfig());

      // Handle Mixpanel identification and published listings count
      return updateMixpanelForCurrentUser(currentUser, sdk);
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications(currentUser));
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());

      const { items } = getState().cart;
      if (!items && (items && Object.keys(items).length == 0)) {
        dispatch(fetchCartItems());
      }
      // // Initiate Intercom for logged in user
      // initiateIntercomForLoggnedUser(sdk, currentUser);

      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(
      new Error('Verification email sending already in progress')
    );
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};


// src/actions/user.js

export const setCUserCurrencyAndLocation = (currentUser) => async (
  dispatch,
  getState,
  sdk
) => {
  // First get details from cookies. Will be overridden by IP or GPS
  const cookieLocation = Cookies.get(LOCATIONINFO_COOKIE_KEY);
  const cookieLocationParsed = cookieLocation ? JSON.parse(cookieLocation) : null;
  let countryCodeFromCookie = cookieLocationParsed?.country || null;
  const userCurrencyFromCookie = Cookies.get(CURRENCY_COOKIE_KEY);
  let detectedLocation = null;
  let countryCode = null;

  // Then get details from IP address or GPS. (IP for country only, GPS for location)
  // If can access, override cookie details
  try {

    const results = await Promise.allSettled([
      fetchUserCountry(),
      getCurrentUserLocationInfo()
    ]);

    if (results[0].status === 'fulfilled') {
      countryCode = results[0].value || countryCodeFromCookie;
    }

    if (results[1].status === 'fulfilled') {
      detectedLocation = results[1].value;
    }

  } catch (error) {
    // silent fail
  } finally {
    // Always clear the location watch after we're done
    clearLocationWatch();
  }

  // Prioritize cookie currency
  let userCurrency = userCurrencyFromCookie || getUserDefaultCurrency(countryCode);
  let userLocationInfo = {
    ...(detectedLocation && Object.keys(detectedLocation).length > 0 ? detectedLocation : cookieLocationParsed),
  };

  // Only override country if we got it from IP and it's not already in detectedLocation
  if (countryCode && (!detectedLocation || !detectedLocation.country)) {
    userLocationInfo.country = countryCode;
  }

  // If user is logged in, and the location and currency info is not available, get it from the profile
  if (currentUser?.id) {
    const { currency: profileCurrency, locationInfo: profileLocation } = extractUserCurrencyAndLocation(currentUser);
    if (!userCurrency) userCurrency = profileCurrency;
    const importantFields = ['address', 'country', 'origin',];
    const locationSet = new Set(Object.keys(userLocationInfo));
    const hasAllImportantFields = importantFields.every(field => locationSet.has(field));
    if (!hasAllImportantFields) {
      userLocationInfo = { ...userLocationInfo, ...profileLocation };
    }
  };

  // Convert location data back to SDK types if it exists
  if (userLocationInfo) {
    // Convert origin to SDK LatLng if it exists
    if (userLocationInfo.origin && userLocationInfo.origin.lat && userLocationInfo.origin.lng) {
      userLocationInfo.origin = new LatLng(userLocationInfo.origin.lat, userLocationInfo.origin.lng);
    }

    // Convert bounds to SDK LatLngBounds if it exists
    if (userLocationInfo.bounds && userLocationInfo.bounds.sw && userLocationInfo.bounds.ne) {
      const { sw, ne } = userLocationInfo.bounds;
      const swLatLng = new LatLng(sw.lat, sw.lng);
      const neLatLng = new LatLng(ne.lat, ne.lng);
      userLocationInfo.bounds = new LatLngBounds(neLatLng, swLatLng);
    }
  }

  // Only store location to cookie as currency should be stored only on manual selection.
  if (!!userLocationInfo) {
    Cookies.set(LOCATIONINFO_COOKIE_KEY, JSON.stringify(userLocationInfo), {
      expires: 30,
      secure: process.env.NODE_ENV === 'production'
    });
  }

  dispatch(setCurrentUserCurrency(userCurrency || DEFAULT_CURRENCY));
  dispatch(setCurrentUserLocationInfo(userLocationInfo));
  dispatch(setDetectedCurrentLocation(detectedLocation));
  return { currentUserCurrency: userCurrency, currentUserLocationInfo: userLocationInfo };
};

export const currentUserPublicDataSelector = state => state.user?.currentUser?.attributes?.profile?.publicData || {};
export const currentUserSelector = state => state.user?.currentUser || {};